import {
  checkAxiosData,
  checkIsJSON,
  filterObjProps,
  getAxiosErr
} from 'fe-common'
import { catchError, empty, from, filter, tap } from 'rxjs'
import { useRouter, useStore } from '@nuxtjs/composition-api'
import { handleMessages } from '../modules/client/utilities'
import { useDayjs } from '../plugins/dayjs'
import useLogout$ from './useLogout$'

export default function useErrorHandler(context) {
  const store = useStore()
  const router = useRouter()
  const dayjs = useDayjs()

  function errorHandler(err, cb) {
    showError(getAxiosErr(err), cb)
  }
  function errorDisplayHandler(axiosErr) {
    return showError(axiosErr, null)
  }
  const { logout$ } = useLogout$(context)
  function showError(axiosErr, cb) {
    const { statusCode, message, code, data, contentType } = axiosErr
    let { maintainProperties } = axiosErr
    // csrf error handler
    if (statusCode === 403 && message === 'Invalid CSRF Token') {
      return context.root.$swal({
        type: 'warning',
        title: context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.403.timeoutOperation'
        ),
        text: context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.403.reconnectAfterThreeSeconds'
        ),
        timer: 3000,
        showCancelButton: false,
        onClose: () => {
          from(store.dispatch('authModule/refreshCSRFToken'))
            .pipe(
              filter(data =>
                checkAxiosData(data, {
                  handleMessages: handleMessages({
                    store
                  })
                })
              ),
              tap(data => store.commit('authModule/CSRF_TOKEN', data.result)),
              catchError(err => errorHandler$(err))
            )
            .subscribe()
        }
      })
    }

    // auth error handler
    if (statusCode === 401) {
      // logging(axiosErr)

      logout$(`/?openLoginModal=true&time=${new Date().getTime()}`)
        .pipe(catchError(err => errorHandler$(err)))
        .subscribe()

      return Promise.resolve()
    }

    // CDN handler
    if (contentType && contentType.indexOf('application/json') === -1) {
      return context.root.$swal({
        type: 'error',
        title: context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.500.failOperation'
        ),
        text: context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.500.theSystemIsBusy'
        )
      })
    }

    // webapi GG handler
    if (
      statusCode === 500 &&
      message &&
      (message.startsWith('connect ECONNREFUSED') ||
        message.startsWith('read ECONNRESET'))
    ) {
      return context.root.$swal({
        type: 'error',
        title: context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.500.failOperation'
        ),
        text: context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.500.theSystemIsBusy'
        )
      })
    }

    // maintain handler
    if (statusCode === 503 && maintainProperties) {
      maintainProperties = atob(maintainProperties)
      if (checkIsJSON(maintainProperties)) {
        maintainProperties = JSON.parse(maintainProperties)
        const startTime = dayjs
          .utc(maintainProperties.startTime)
          .tz()
          .format('HH:mm')
        const endTime = dayjs
          .utc(maintainProperties.endTime)
          .tz()
          .format('HH:mm')
        return context.root.$swal({
          type: 'warning',
          title: context.root.$t(
            'component.mixin.mixinErrorHandler.method.errorHandler.500.failOperation'
          ),
          text: context.root.$t(`common.maintainMessage`, [startTime, endTime])
        })
      }
    }

    // axios multi request handler
    if (message === 'Operation canceled due to new request.') {
      return Promise.resolve()
    }

    if (typeof code !== 'undefined' && code !== 0) {
      axiosErr.message = responseCodeToMessage(code, data)
    }

    if (cb && cb(axiosErr)) {
      return Promise.resolve()
    }

    if (statusCode) {
      if (![400, 405, 412, 503].includes(statusCode)) {
        logging(axiosErr)
      }
    } else {
      logging(axiosErr)
    }

    const swal = (
      title = context.root.$t(
        'component.mixin.mixinErrorHandler.method.errorHandler.other.failOperation'
      ),
      html = axiosErr?.message ||
        context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.other.networkError'
        )
    ) =>
      context.root.$swal({
        type: 'error',
        title: `<div class="d-flex flex-column">${title}<small class="text-danger error-code-tip mt-1${
          code === 0 ? ' d-none' : ''
        }">（Error Code: ${code}）</small></div>`, // swal 會在換行自動幫你增加 <br>，所以這裏不允許對 html 美化排版
        html
      })

    if (
      [
        412002,
        412427,
        412428,
        412429,
        412430,
        412753,
        412742,
        412847,
        412407,
        412397,
        412398
      ].includes(code)
    ) {
      return swal().then(result => {
        if (result.value) {
          router.push('/user/account-setting/personal-data')
        }
        return result
      })
    }
    if ([412396, 412355, 412439].includes(code)) {
      return swal().then(result => {
        if (result.value) {
          router.push('/user/account-setting/kyc')
        }
        return result
      })
    }
    // customize message
    if ([412439, 412379].includes(axiosErr.code)) {
      return swal(
        context.root.$t(
          'component.mixin.mixinErrorHandler.method.errorHandler.other.kyc'
        )
      ).then(result => {
        if (result.value) {
          router.push('/user/account-setting/kyc')
        }
        return result
      })
    }
    return swal()
  }
  function logging(err, loggingFields = []) {
    // condition global variable of $sentry
    if (!context.root.$sentry) return

    // formatting err
    if (!(err && err.stack && err.message)) {
      err = new Error(
        JSON.stringify(
          loggingFields.length > 0 ? filterObjProps(err, loggingFields) : err
        )
      )
      err.name = 'UnknownError'
    }

    // filter message
    if (err.message.match(/The connection has not been established yet/i))
      return
    if (err.message.match(/The operation is insecure/i)) return
    if (err.message.match(/Request aborted/i)) return
    if (err.message.match(/Connection refused/i)) return
    if (err.message.match(/NavigationDuplicated/i)) return
    if (err.message.match(/Network Error/i)) return
    if (err.message.match(/网站维护中/i)) return
    if (err.message.match(/Request failed with status code (401|503|504)/i))
      return
    if (err.message.match(/Cannot read property 'substr' of undefined/i)) return
    if (
      err.message.match(/Cannot set headers after they are sent to the client/i)
    )
      return
    if (
      err.message.match(
        /undefined is not an object \(evaluating 'document\.getElementsByTagName\('video'\)\[0]\.src'\)/i
      )
    )
      return

    // logging to sentry
    context.root.$sentry.captureException(err)
  }
  function responseCodeToMessage(errorCode, params = []) {
    const hasParamsRegex = RegExp(/^(413|\D)/)
    let errorMsg

    if (Array.isArray(params)) {
      params = params.map(param => {
        // convert error code inside params to message RECURSIVELY
        if (typeof param === 'object') {
          if (context.root.$te(`webApiErrors.${param.code}`)) {
            return responseCodeToMessage(param.code, param.data)
          }

          return param.data
        } else {
          return param
        }
      })
    }

    // error code with params
    if (hasParamsRegex.test(errorCode)) {
      errorMsg = context.root.$t(`webApiErrors.${errorCode}`, params)
    } else {
      errorMsg = context.root.$t(`webApiErrors.${errorCode}`)
    }

    return errorMsg
  }

  // rxjs operator: catchError to use
  function errorHandler$(err, cb) {
    showError(getAxiosErr(err), cb)
    return empty()
  }

  return {
    errorHandler,
    errorDisplayHandler,
    showError,
    logging,
    responseCodeToMessage,
    errorHandler$
  }
}
